import React, { useState, useMemo } from 'react';
import { Box, Paper, Typography, Button, Link, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Card } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SecurityIcon from '@mui/icons-material/Security';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { usePricing } from '../../../hooks/usePricing';
import { convertCentsToEuros } from '../../../services/transformers';
import { packages as packageConstants } from '../../plans/constants';
import type { Package } from '../../../services/payment.types';

interface TransactionCountData {
    count: number;
    isLoading: boolean;
}

interface PurchaseActionsBoxProps {
    selectedYear: string;
    allYears: string[];
    isPurchased?: boolean;
    onBuyYear?: (year: string) => void;
    onBuyAllYears?: () => void;
    isLoading?: boolean;
    transactionCounts?: Record<string, TransactionCountData>;
}

const PurchaseActionsBox: React.FC<PurchaseActionsBoxProps> = ({
    selectedYear,
    allYears,
    isPurchased = false,
    onBuyYear,
    onBuyAllYears,
    isLoading = false,
    transactionCounts = {}
}) => {
    const { t } = useTranslation();
    const { pricing, isLoading: isPricingLoading } = usePricing('DE');
    const [showPricingModal, setShowPricingModal] = useState(false);

    // Merge pricing data with package constants to create full Package objects
    const packagesWithPricing = useMemo(() => {
        if (!pricing?.packages) return packageConstants;

        return packageConstants.map((pkg) => {
            const pricingData = pricing.packages[pkg.id as keyof typeof pricing.packages];
            return {
                ...pkg,
                price: pricingData?.price ? convertCentsToEuros(pricingData.price) : pkg.price,
                transactions: pricingData?.transactions ?? pkg.transactions,
            };
        });
    }, [pricing?.packages]);

    // Utility function to determine package tier based on transaction count
    const getPackageForTransactions = (transactionCount: number): Package => {
        const suitablePackages = packagesWithPricing
            .filter(pkg => pkg.transactions >= transactionCount)
            .sort((a, b) => a.price - b.price);
        
        // Return the cheapest suitable package, or enterprise if none can handle the transaction count
        return suitablePackages.length > 0 ? suitablePackages[0] : packagesWithPricing[packagesWithPricing.length - 1];
    };

    // Calculate pricing for individual year
    const selectedYearTransactionCount = transactionCounts[selectedYear]?.count || 0;
    const selectedYearPackage = getPackageForTransactions(selectedYearTransactionCount);
    const singleYearPrice = selectedYearPackage.price;

    // Calculate pricing for all years
    const allYearsPricing = allYears.map(year => {
        const yearTransactionCount = transactionCounts[year]?.count || 0;
        const yearPackage = getPackageForTransactions(yearTransactionCount);
        return {
            year,
            transactionCount: yearTransactionCount,
            package: yearPackage,
            price: yearPackage.price
        };
    });
    const allYearsPrice = allYearsPricing.reduce((total, yearData) => total + yearData.price, 0);

    // Check if any transaction count data is still loading
    const isTransactionCountLoading = Object.values(transactionCounts).some(data => data.isLoading);

    if (isPurchased) {
        return (
            <Card data-purchase-box sx={{}}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
                    <Typography variant="h6" color="success.dark">
                        {t('taxReports.planPurchased', 'Plan erworben!')}
                    </Typography>
                </Box>
            </Card>
        );
    }

    return (
        <Card data-purchase-box sx={{}}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                <Typography variant="h6" color="text.primary">
                    {t('taxReports.unlockReport', 'Steuerbericht freischalten')}
                </Typography>
            </Box>

            {/* Trust badges above buttons */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                gap: 2,
                mb: 2.5,
                flexWrap: 'wrap'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <FlashOnIcon sx={{ color: 'success.main', fontSize: 14 }} />
                    <Typography variant="caption" color="text.secondary">
                        {t('taxReports.instantAccess', 'Sofortiger Zugang')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <SecurityIcon sx={{ color: 'success.main', fontSize: 14 }} />
                    <Typography variant="caption" color="text.secondary">
                        {t('taxReports.securePayment', 'Sichere Zahlung')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <Typography variant="caption" color="text.secondary">
                        🔒 {t('taxReports.noSubscription', 'Keine Abonnements • Einmalzahlung')}
                    </Typography>
                </Box>
            </Box>

            {/* CTA Buttons - Side by side */}
            <Box sx={{ 
                display: 'flex', 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                mb: 2
            }}>
                {/* Single Year Option */}
                <Button
                    variant="contained"
                    size="large"
                    onClick={() => onBuyYear?.(selectedYear)}
                    disabled={isLoading || isPricingLoading || isTransactionCountLoading}
                    sx={{
                        flex: 1
                    }}
                >
                    {(isPricingLoading || isTransactionCountLoading) 
                        ? t('common.loading', 'Laden...') 
                        : `Steuerbericht ${selectedYear} – €${singleYearPrice.toFixed(2)}`
                    }
                </Button>

                {/* All Years Option */}
                {allYears.length > 1 && (
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={onBuyAllYears}
                        disabled={isLoading || isPricingLoading || isTransactionCountLoading}
                        sx={{
                            flex: 1
                        }}
                    >
                        {(isPricingLoading || isTransactionCountLoading) 
                            ? t('common.loading', 'Laden...') 
                            : `Alle verfügbaren Jahre – €${allYearsPrice.toFixed(2)}`
                        }
                    </Button>
                )}
            </Box>

            {/* What's included - redesigned with better alignment and conversion focus */}
            <Box sx={{ 
                p: 2, 
                mb: 1.5 
            }}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                    gap: 2,
                    alignItems: 'start'
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1,
                    }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.1 }} />
                        <Typography variant="body2">
                            {t('taxReports.taxRelevantSums', 'Steuerrelevante Summen im Überblick')}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1,
                    }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.1 }} />
                        <Typography variant="body2">
                            {t('taxReports.completeTransactionOverview', 'Komplette Transaktionsübersicht')}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1,
                    }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.1 }} />
                        <Typography variant="body2">
                            {t('taxReports.automaticProfitLossCalc', 'Automatische Gewinn- & Verlustberechnung')}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1,
                    }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.1 }} />
                        <Typography variant="body2">
                            {t('taxReports.clearCalculationRules', 'Klare Darstellung der Berechnungsregeln')}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1,
                    }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.1 }} />
                        <Typography variant="body2">
                            {t('taxReports.bmfCompliantShort', 'BMF-konform inkl. Anlage SO & KAP')}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 1,
                    }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mt: 0.1 }} />
                        <Typography variant="body2">
                            {t('taxReports.pdfExcelDownload', 'PDF- & Excel-Download')}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Pricing explanation link */}
            <Box sx={{ textAlign: 'center' }}>
                <Link
                    component="button"
                    variant="caption"
                    onClick={() => setShowPricingModal(true)}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        border: 'none',
                        background: 'none',
                        p: 0
                    }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                    {t('taxReports.howIsThisCalculated', 'Wie wird der Preis berechnet?')}
                </Link>
            </Box>

            {/* Pricing Explanation Modal - Compact Single-Row Design */}
            <Dialog 
                open={showPricingModal} 
                onClose={() => setShowPricingModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {t('taxReports.pricingExplanation', 'Preisberechnung')}
                    <IconButton onClick={() => setShowPricingModal(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ py: 1 }}>
                        {/* 1. Pläne Section */}
                        <Typography variant="h6" sx={{ mb: 1.5 }}>
                            {t('taxReports.packageTiers', 'Pläne')}
                        </Typography>
                        
                        <Box sx={{ mb: 3 }}>
                            {packagesWithPricing.map((pkg, index) => (
                                <Box 
                                    key={pkg.id} 
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        py: 0.75,
                                        px: 1,
                                        backgroundColor: index % 2 === 0 
                                        ? (theme) => theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.05)'
                                            : 'rgba(0,0,0,0.02)'
                                        : 'transparent'
                                    }}
                                >
                                    <Typography variant="body2">
                                        {pkg.name} (bis {pkg.transactions.toLocaleString('de-DE')} Transaktionen)
                                    </Typography>
                                    <Typography variant="body2">
                                        €{pkg.price.toFixed(2)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* 2. Selected Year Section */}
                        <Typography variant="h6" sx={{ mb: 1.5 }}>
                            Steuerbericht {selectedYear}
                        </Typography>
                        
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                py: 0.75,
                                px: 1,
                                backgroundColor: 'rgba(0,0,0,0.02)'
                            }}>
                                <Typography variant="body2">
                                    {selectedYearTransactionCount.toLocaleString('de-DE')} Transaktionen ({selectedYearPackage.name})
                                </Typography>
                                <Typography variant="body2">
                                    €{singleYearPrice.toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* 3. All Years Section */}
                        {allYears.length > 1 && (
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1.5 }}>
                                    Alle verfügbare Jahre
                                </Typography>
                                
                                <Box sx={{ mb: 1 }}>
                                    {allYearsPricing.map((yearData, index) => (
                                        <Box 
                                            key={yearData.year} 
                                            sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                py: 0.75,
                                                px: 1,
                                                backgroundColor: index % 2 === 0 
                                        ? (theme) => theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.05)'
                                            : 'rgba(0,0,0,0.02)'
                                        : 'transparent'
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {yearData.year}: {yearData.transactionCount.toLocaleString('de-DE')} Transaktionen ({yearData.package.name})
                                            </Typography>
                                            <Typography variant="body2">
                                                €{yearData.price.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                                
                                {/* Total Row */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    py: 0.75,
                                    px: 1,
                                    backgroundColor: 'rgba(0,0,0,0.06)',
                                    mt: 0.5
                                }}>
                                    <Typography variant="body2">
                                        {t('taxReports.total', 'Gesamt')}
                                    </Typography>
                                    <Typography variant="body2">
                                        €{allYearsPrice.toFixed(2)}
                                    </Typography>
                                </Box>
                                
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                                    {t('taxReports.noDiscountExplanation', 'Jedes Jahr wird basierend auf seinen Transaktionen einzeln berechnet.')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowPricingModal(false)} variant="outlined">
                        {t('common.close', 'Schließen')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default PurchaseActionsBox;